import React, { useState, useEffect } from 'react'
import { Modal, Button, Checkbox, message } from 'antd'
import { ThunderboltOutlined } from '@ant-design/icons'
import { ApiRequest } from '../../../../services/api/api'
import SweetAlertService from '../../../../services/lib/utils/sweet-alert-service'
import { modalStyles, titleStyle, indicatorStyle, tableHeaderStyle, buttonStyles, tableBorder, tableCellStyle, modalContainerStyle } from './modal-styles'

interface ReceivingItem {
  poDetailId: number
  itemCode: string
  description: string
  receivingQty: number
  checked: boolean
}

interface MergeToPalletModalProps {
  visible: boolean
  onClose: () => void
  poData: any
  selectedItems: any[]
  onSuccess: (palletResult: any) => void
}

const MergeToPalletModal: React.FC<MergeToPalletModalProps> = ({
  visible,
  onClose,
  poData,
  selectedItems,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false)
  const [itemList, setItemList] = useState<ReceivingItem[]>([])
  const [selectAll, setSelectAll] = useState(true)
  const [itemDetailsMap, setItemDetailsMap] = useState<Map<number, any>>(new Map())

  // 获取 Item 详情（从 GetAllUnloadingInspection 获取）
  const fetchItemDetails = async () => {
    if (!poData?.poId) return

    try {
      const inspectionResponse = await ApiRequest({
        url: `UnloadingInspection/GetAllUnloadingInspection?poId=${poData.poId}`,
        method: 'get',
        isShowSpinner: false
      })

      if (inspectionResponse?.data?.data && inspectionResponse.data.data.length > 0) {
        const inspectionData = inspectionResponse.data.data[0]
        const newItemDetailsMap = new Map<number, any>()

        if (inspectionData.unloadingDetail && Array.isArray(inspectionData.unloadingDetail)) {
          inspectionData.unloadingDetail.forEach((detail: any) => {
            const poDetailItem = selectedItems?.find((item: any) =>
              item.rawMaterial?.rawMaterialId === detail.rawMaterialId
            )
            if (poDetailItem) {
              newItemDetailsMap.set(poDetailItem.poDetailId, {
                rawMaterialId: detail.rawMaterialId,
                itemCode: poDetailItem.rawMaterial?.rawMaterialCode || '',
                description: poDetailItem.rawMaterial?.rawMaterialName || '',
                revAreaQty: detail.revAreaQty || 0
              })
            }
          })
        }

        setItemDetailsMap(newItemDetailsMap)
      }
    } catch (error) {
      // Silently handle fetch errors
    }
  }

  useEffect(() => {
    if (visible && selectedItems) {
      // 获取 Item 详情
      fetchItemDetails()

      // 初始化物料列表，默认全部选中
      const initialItems = selectedItems.map(item => {
        return {
          poDetailId: item.poDetailId,
          itemCode: item.rawMaterial?.rawMaterialCode || 'N/A',
          description: item.rawMaterial?.rawMaterialName || 'N/A',
          receivingQty: 0,  // 将从 itemDetailsMap 获取
          checked: true
        }
      })
      setItemList(initialItems)
      setSelectAll(true)
    }
  }, [visible, selectedItems])

  // 当 itemDetailsMap 更新时，更新 itemList 中的 receivingQty
  useEffect(() => {
    if (itemDetailsMap.size > 0 && itemList.length > 0) {
      const updatedItemList = itemList.map(item => {
        const detail = itemDetailsMap.get(item.poDetailId)
        return {
          ...item,
          receivingQty: detail?.revAreaQty || 0
        }
      })
      setItemList(updatedItemList)
    }
  }, [itemDetailsMap])

  // 全选处理
  const handleSelectAllChange = (checked: boolean) => {
    setSelectAll(checked)
    const newItemList = itemList.map(item => ({
      ...item,
      checked: checked
    }))
    setItemList(newItemList)
  }

  // 单个复选框处理
  const handleItemSelectChange = (index: number, checked: boolean) => {
    const newItemList = [...itemList]
    newItemList[index].checked = checked
    setItemList(newItemList)

    // 更新全选状态
    const allChecked = newItemList.every(item => item.checked)
    setSelectAll(allChecked)
  }

  // 执行合并操作
  const handleMerge = async () => {
    const checkedItems = itemList.filter(item => item.checked)

    if (checkedItems.length < 2) {
      message.error('Please select at least 2 items to merge')
      return
    }

    // 验证是否有 Receiving Qty 数据
    const itemsWithQty = checkedItems.filter(item => item.receivingQty > 0)

    if (itemsWithQty.length === 0) {
      message.error('Cannot operate without product')
      return
    }

    // 如果部分物料没有数量，给出警告
    if (itemsWithQty.length < checkedItems.length) {
      const itemsWithoutQty = checkedItems.filter(item => item.receivingQty === 0)
      const itemCodes = itemsWithoutQty.map(item => item.itemCode).join(', ')
      message.warning(`The following items have no Receiving Qty and will be skipped: ${itemCodes}`)
    }

    setLoading(true)

    try {
      // 准备合并数据 - 只包含有数量的物料
      const mergeData = {
        items: itemsWithQty.map(item => ({
          poDetailId: item.poDetailId,
          revQty: item.receivingQty
        })),
        plateTypeId: 2
      }

      // 调用合并 API
      const response = await ApiRequest({
        url: 'PurchaseOrder/MergeItemsToPallet',
        method: 'post',
        data: mergeData,
        isShowSpinner: true
      })

      // 获取返回的托盘信息
      const palletResult = response?.data?.data

      // 关闭当前弹窗
      onClose()

      // 调用成功回调，传递托盘结果和合并的物料
      onSuccess({
        palletResult: palletResult,
        mergedItems: itemsWithQty.map(item => ({
          poDetailId: item.poDetailId,
          rawMaterial: {
            rawMaterialCode: item.itemCode,
            rawMaterialName: item.description
          },
          totalRevQty: item.receivingQty
        }))
      })
    } catch (error) {
      message.error('Merge failed')
    } finally {
      setLoading(false)
    }
  }

  // 取消操作
  const handleCancel = () => {
    setItemList([])
    setSelectAll(true)
    onClose()
  }

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleCancel}
      width={1000}
      footer={null}
      closable={true}
      style={{ top: 50 }}
      bodyStyle={{ padding: modalStyles.padding, maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
    >
      <div style={modalContainerStyle}>
        {/* 页头标题 */}
        <div style={titleStyle}>
          Merge to Pallet
        </div>

        {/* 主内容区域 */}
        <div style={{
          backgroundColor: modalStyles.bgWhite,
          border: `1px solid ${modalStyles.borderSecondary}`,
          borderRadius: modalStyles.borderRadiusSmall,
          padding: modalStyles.paddingSmall,
          marginBottom: '20px'
        }}>
          {/* 子标签栏 */}
          <div style={{
            borderBottom: `2px solid ${modalStyles.borderSecondary}`,
            marginBottom: '16px',
            paddingBottom: '12px'
          }}>
            <span style={{
              fontSize: '14px',
              fontWeight: 600,
              color: modalStyles.warning,
              borderBottom: `2px solid ${modalStyles.warning}`,
              paddingBottom: '10px',
              display: 'inline-block'
            }}>
              Item List
            </span>
          </div>

          {/* 表格 */}
          <table style={{ width: '100%', ...tableBorder, borderRadius: modalStyles.borderRadiusSmall, overflow: 'hidden', boxShadow: modalStyles.shadowSmall }}>
            <thead>
              <tr style={tableHeaderStyle}>
                <th style={{ ...tableCellStyle, textAlign: 'center', width: '60px' }}>
                  <Checkbox
                    checked={selectAll}
                    onChange={(e) => handleSelectAllChange(e.target.checked)}
                  />
                </th>
                <th style={{ ...tableCellStyle, textAlign: 'left' }}>Item Code</th>
                <th style={{ ...tableCellStyle, textAlign: 'left' }}>Description</th>
                <th style={{ ...tableCellStyle, textAlign: 'center' }}>Receiving Qty</th>
              </tr>
            </thead>
            <tbody>
              {itemList.map((item, index) => (
                <tr key={item.poDetailId} style={{ backgroundColor: index % 2 === 0 ? modalStyles.bgWhite : modalStyles.bgGray }}>
                  <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                    <Checkbox
                      checked={item.checked}
                      onChange={(e) => handleItemSelectChange(index, e.target.checked)}
                    />
                  </td>
                  <td style={{ ...tableCellStyle, color: modalStyles.textPrimary, fontWeight: 500 }}>
                    {item.itemCode}
                  </td>
                  <td style={{ ...tableCellStyle, color: modalStyles.textPrimary }}>
                    <div style={{ wordBreak: 'break-word' }}>
                      {item.description}
                    </div>
                  </td>
                  <td style={{ ...tableCellStyle, textAlign: 'center' }}>
                    <div style={{
                      display: 'inline-block',
                      padding: '6px 12px',
                      backgroundColor: modalStyles.bgGray,
                      border: `1px solid ${modalStyles.borderSecondary}`,
                      borderRadius: modalStyles.borderRadiusTiny,
                      fontSize: '14px',
                      fontWeight: 500,
                      color: modalStyles.textPrimary,
                      minWidth: '60px'
                    }}>
                      {item.receivingQty}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* 数量统计信息 */}
          <div style={{
            marginTop: '12px',
            padding: '12px',
            backgroundColor: '#fffbeb',
            border: `1px solid ${modalStyles.warning}`,
            borderRadius: modalStyles.borderRadiusSmall,
            fontSize: '14px',
            color: '#92400e',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>
              <strong>Selected:</strong> {itemList.filter(item => item.checked).length} / {itemList.length} items
            </span>
            <span>
              <strong>Total Qty:</strong> {itemList.filter(item => item.checked).reduce((sum, item) => sum + item.receivingQty, 0)}
            </span>
          </div>
        </div>

        {/* 底部操作按钮区 */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: `2px solid ${modalStyles.borderSecondary}` }}>
          <Button
            onClick={handleCancel}
            style={{
              ...buttonStyles.secondary,
              minWidth: '120px',
              lineHeight: '1.5',
              padding: '8px 20px',
              fontSize: '14px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = modalStyles.bgLight
              e.currentTarget.style.borderColor = modalStyles.primaryDark
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff'
              e.currentTarget.style.borderColor = modalStyles.primary
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleMerge}
            loading={loading}
            style={{
              ...buttonStyles.warning,
              minWidth: '120px',
              lineHeight: '1.5',
              padding: '8px 20px',
              fontSize: '14px',
              position: 'relative'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 158, 11, 0.4)'
                e.currentTarget.style.transform = 'translateY(-1px)'
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.boxShadow = buttonStyles.warning.boxShadow
                e.currentTarget.style.transform = 'translateY(0)'
              }
            }}
          >
            Merge
            <ThunderboltOutlined style={{
              position: 'absolute',
              top: '4px',
              right: '8px',
              fontSize: '13px',
              color: '#fcd34d'
            }} />
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default MergeToPalletModal
